/* @flow */
import * as React from 'react';
import { Provider } from 'react-redux';
import {
  Router,
  Route,
  IndexRoute,
  IndexRedirect,
  Redirect
} from 'react-router';

/**
 * NOTE: App should not be lazy-loaded. It is the root UI component and lazy
 * loading seems to break when trying to load it. Perhaps it's react router and
 * something to do with this being the root route.  Either way, lazy loading all
 * _other_ components already provides a big win.
 */
import App from './components/App.js';

import ConnectedDesignSystemProvider from './components/ConnectedDesignProvider';

/* Import Bundled Components */
import {
  CreateContainer,
  UpdateContainer,
  CreateForm,
  Review,
  CreateIndicators,
  SettingsContainer,
  ApiSettingsContainer,
  EmailSettingsContainer,
  MarketplaceContainer,
  ProfileSettings,
  RedactionLibrary,
  CompanyWhitelist,
  CompaniesContainer,
  SharingGroupsContainer,
  TemplatesContainer,
  SubscriptionsContainer,
  EnclavesContainer,
  UsersContainer,
  MalwareContainer,
  ThreatActorsContainer,
  GlobalWhitelist,
  EntitiesBrowser,
  ReportBrowser,
  CaseBrowser,
  SearchBrowser,
  PageContainer,
  About,
  Help,
  Terms,
  Tours,
  ErrorView,
  NotFound,
  Login,
  Logout,
  EnsureLogin,
  Mfa,
  TermsAgreement,
  CreatePassword,
  ForgotPassword,
  AuthContainer,
  NotificationSettings,
  SSOReceiver,
  ConstellationContainerV2,
  PhishingView,
} from './BundleCreator';

import {
  ToggleFeatureView,
  FeatureNotEnabledView
} from 'trustar-2.0/containers/features';
import { flags as Flags, treatments as Treatments } from 'modules/features';

const makeRoutes = () => (
  <Route path='/'>
    <IndexRedirect to='/browse/reports' />
    <Redirect from='*.html' to='/' />

    <Route component={EnsureLogin}>
      <Route component={App}>
        {/* Constellation */}
        <Redirect from='reports' to='/browse/reports' />
        <Redirect from='constellation' to='/browse/reports' />
        <Redirect from='reports/:id' to='/constellation/reports/:id' />
        <Route
          path='constellation/:viewType/:id'
          component={ConstellationContainerV2}
        />

        <Route component={ConnectedDesignSystemProvider}>
          <Route path='error' component={ErrorView} />

          <Route path='browse'>
            <IndexRedirect to='reports' />
            <Route path='iocs' component={EntitiesBrowser} />
            <Route path='reports' component={ReportBrowser} />
            <Route
              path='cases'
              component={ToggleFeatureView}
              toggledComponent={CaseBrowser}
              untoggledComponent={FeatureNotEnabledView}
              feature={Flags.CASES_VIEW_ENABLED}
              match={Treatments.ON}
            />
            <Route path='search' component={SearchBrowser} />
            <Route path='marketplace' component={MarketplaceContainer} />
            <Redirect from='tours' to='reports' />
          </Route>
        </Route>

        <Route
          path='phishing'
          component={ToggleFeatureView}
          toggledComponent={PhishingView}
          untoggledComponent={FeatureNotEnabledView}
          feature={Flags.PHISHING_TRIAGE_ENABLED}
          match={Treatments.ON}
        />

        {/* Create Report */}
        <Route path='create' component={CreateContainer}>
          <IndexRoute component={CreateForm} />
          <Route path='review' component={Review} />
        </Route>

        {/* Create Indicators */}
        <Route path='create/iocs' component={CreateIndicators} />

        {/* Update Report */}
        <Route path='update/:id' component={UpdateContainer}>
          <IndexRoute component={CreateForm} />
          <Route path='review' component={Review} />
        </Route>

        {/* Complex Copy Report */}
        <Route
          path='update/:id/enclave/:destEnclaveGuid'
          component={UpdateContainer}>
          <IndexRoute component={CreateForm} />
          <Route path='review' component={Review} />
        </Route>

        {/* Settings */}
        <Route path='settings' component={SettingsContainer}>
          <IndexRedirect to='profile' />
          <Route path='profile' component={ProfileSettings} />
          <Route path='users' component={UsersContainer} />
          <Route path='api' component={ApiSettingsContainer} />
          <Route path='email' component={EmailSettingsContainer} />
          <Route path='redaction' component={RedactionLibrary} />
          <Route path='templates' component={TemplatesContainer} />
          <Route path='subscriptions' component={SubscriptionsContainer} />
          <Route path='enclaves' component={EnclavesContainer} />
          <Route path='users' component={UsersContainer} />
          <Route component={ConnectedDesignSystemProvider}>
            <Route path='whitelist' component={CompanyWhitelist} />
            <Route path='global-whitelist' component={GlobalWhitelist} />
            <Route path='malware' component={MalwareContainer} />
            <Route path='threat-actors' component={ThreatActorsContainer} />
          </Route>
          <Route path='companies' component={CompaniesContainer} />
          <Route path='sharing-groups' component={SharingGroupsContainer} />
          <Route path='notifications' component={NotificationSettings} />
        </Route>

        {/* Static pages */}
        <Redirect from='pages' to='/pages/help' />
        <Route path='pages' component={PageContainer}>
          <Route path='about' component={About} />
          <Route path='help' component={Help} />
          <Route path='terms' component={Terms} />
          <Route path='*' component={NotFound} />
        </Route>
      </Route>
    </Route>

    <Route component={ConnectedDesignSystemProvider}>
      <Route path='auth' component={AuthContainer}>
        <Route path='login' component={Login} />
        <Route path='logout' component={Logout} />
        <Route path='mfa' component={Mfa} />
        <Route path='eula' component={TermsAgreement} />
        <Route path='create-password' component={CreatePassword} />
        <Route path='forgot-password' component={ForgotPassword} />
        <Route path='sso' component={SSOReceiver} />
        <Redirect from='*' to='login' />
      </Route>
    </Route>

    {/* Catch All */}
    <Route path='*' component={NotFound} />
  </Route>
);

/**
 * The root of the application. This file stands alone so that we can enable HMR
 * and rerender the app root. For the app ENTRY POINT see index.js.
 *
 * NOTE: This component will only render once in prod. Because of this it should
 * be fine to re-create the routes within the render method. In development it
 * will be re-rendered whenever the app changes.
 */
export default class Root extends React.Component {
  props: {
    store: Object,
    history: Object
  };

  render() {
    return (
      <Provider store={this.props.store}>
        <ConnectedDesignSystemProvider noBootstrap>
          <Router history={this.props.history}>{makeRoutes()}</Router>
        </ConnectedDesignSystemProvider>
      </Provider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Root.js